import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { instance } from '~/utils/api/api';
import { useToastError } from '~/utils/useToastError';
import { useToastSuccess } from '~/utils/useToastSuccess';
import Heading from '~/components/Shared/Heading/Heading';
import Page from '~/components/Shared/Layout/Page/Page';
import { Pagination, Spinner } from '~/ui';
import { useState } from 'react';
import CloseIcon from '~/assets/svg/newSvg/close-cross.svg?react';
import { Modal } from '~/components/Shared/Modal/Modal';
import styles from './PhotoStoragePhotos.module.scss';

interface IPhoto {
  id: number;
  photo_id: number;
  asset_id: number;
  module: string;
  url: string;
}

const PhotoStoragePhotos = () => {
  const { t } = useTranslation();
  const { module, assetId } = useParams<{ module: string; assetId: string }>();
  const toastError = useToastError();
  const toastSuccess = useToastSuccess();
  const client = useQueryClient();
  const [page, setPage] = useState(1);
  const [selectedPhoto, setSelectedPhoto] = useState<IPhoto | null>(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState<number | null>(null);

  const moduleName = t(`photo_storage_${module}`);

  const { data: photos, isLoading } = useQuery({
    queryFn: async () => {
      const { data } = await instance.get(
        `admin/photo-storage/${module}/${assetId}`,
        { params: { perPage: 20, page } }
      );
      return data;
    },
    queryKey: ['photo-storage-photos', module, assetId, page],
    onError: error => toastError(error),
  });

  const { mutateAsync: deletePhoto } = useMutation(
    async (id: number) => {
      await instance.delete(`admin/photo-storage/${id}`);
    },
    {
      onSuccess: () => {
        toastSuccess(t('deleted_successfully'));
        client.invalidateQueries(['photo-storage-photos']);
        client.invalidateQueries(['photo-storage-assets']);
        client.invalidateQueries(['photo-storage-modules']);
        setDeleteConfirmId(null);
      },
      onError: error => toastError(error),
    }
  );

  return (
    <Page heading={<Heading text={`${t('photo_storage')} — ${moduleName}`} />}>
      <div className={styles.container}>
        {isLoading ? (
          <Spinner />
        ) : (
          <>
            <div className={styles.grid}>
              {(photos?.data || []).map((photo: IPhoto) => (
                <div key={photo.id} className={styles.photoCard}>
                  <button
                    type="button"
                    className={styles.photoButton}
                    onClick={() => setSelectedPhoto(photo)}
                  >
                    <img
                      src={photo.url}
                      alt=""
                      className={styles.photoImage}
                    />
                  </button>
                  <button
                    type="button"
                    className={styles.deleteButton}
                    onClick={() => setDeleteConfirmId(photo.id)}
                  >
                    <CloseIcon className={styles.deleteIcon} />
                  </button>
                </div>
              ))}
              {!photos?.data?.length && (
                <p className={styles.empty}>{t('no_data')}</p>
              )}
            </div>
            <div className={styles.paginationWrapper}>
              <Pagination
                pageCount={photos?.last_page || 1}
                onPageChange={(selectedPage: { selected: number }) =>
                  setPage(selectedPage.selected + 1)
                }
              />
            </div>
          </>
        )}
      </div>

      {/* Просмотр фото */}
      <Modal
        modalStyles={styles.previewModal}
        isOpen={!!selectedPhoto}
        onClose={() => setSelectedPhoto(null)}
      >
        <div className={styles.previewWrapper}>
          {selectedPhoto && (
            <img
              src={selectedPhoto.url}
              alt=""
              className={styles.previewImage}
            />
          )}
        </div>
      </Modal>

      {/* Подтверждение удаления */}
      <Modal
        isOpen={deleteConfirmId !== null}
        onClose={() => setDeleteConfirmId(null)}
      >
        <div className={styles.confirmWrapper}>
          <p className={styles.confirmTitle}>{t('sure_delete_file')}</p>
          <p className={styles.confirmDescription}>
            {t('photo_will_be_deleted_everywhere')}
          </p>
          <div className={styles.confirmButtons}>
            <button
              type="button"
              className={styles.cancelButton}
              onClick={() => setDeleteConfirmId(null)}
            >
              {t('cancel')}
            </button>
            <button
              type="button"
              className={styles.confirmDeleteButton}
              onClick={() => deleteConfirmId && deletePhoto(deleteConfirmId)}
            >
              {t('delete')}
            </button>
          </div>
        </div>
      </Modal>
    </Page>
  );
};

export default PhotoStoragePhotos;
